import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { getBreedsByPetName } from '@/app/actions';
import debounce from 'lodash.debounce';
import { ChevronDown24Regular } from '@fluentui/react-icons';

// How far a finger may travel and still count as a tap rather than a scroll.
const TAP_SLOP_PX = 10;

const SearchSelectBreed: React.FC<{ petType: string; setBreed: (breed: string) => void }> = ({ petType, setBreed }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [displayValue, setDisplayValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedValue, setSelectedValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingTapRef = useRef<
    { pointerId: number; x: number; y: number; suggestion: string } | null
  >(null);

  const clearBlurTimeout = () => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
  };

  const { data, error } = useQuery({
    queryKey: ['breeds', petType, searchTerm],
    queryFn: () => getBreedsByPetName(petType, 1, 200, undefined, true),
    enabled: searchTerm.length >= 0,
  });

  // Derived, not state: a separate suggestions state was being wiped by the petType reset
  // effect in the same commit whenever breed data came from cache, hiding the dropdown.
  const suggestions = useMemo<string[]>(() => {
    return (
      data?.items
        ?.map((item: any) => item.friendlyName || item.name)
        .filter((suggestion: string) =>
          suggestion.toLowerCase().includes(searchTerm.toLowerCase())
        )
        .sort((a: string, b: string) => {
          const aLower = a.toLowerCase();
          const bLower = b.toLowerCase();
          const searchTermLower = searchTerm.toLowerCase();

          const aStartsWithSearchTerm = aLower.startsWith(searchTermLower);
          const bStartsWithSearchTerm = bLower.startsWith(searchTermLower);

          if (aStartsWithSearchTerm && !bStartsWithSearchTerm) {
            return -1;
          } else if (!aStartsWithSearchTerm && bStartsWithSearchTerm) {
            return 1;
          } else {
            return aLower.localeCompare(bLower);
          }
        }) || []
    );
  }, [data, searchTerm]);

  // Type-ahead hint shown over the input.
  useEffect(() => {
    setSelectedValue(
      suggestions.length > 0 && searchTerm.length > 0 ? suggestions[0] : ''
    );
  }, [suggestions, searchTerm]);

  useEffect(() => {
    clearBlurTimeout();
    setSearchTerm('');
    setDisplayValue('');
    setShowSuggestions(false);
    setSelectedValue('');
  }, [petType]);

  useEffect(() => clearBlurTimeout, []);

  // Rendered in a body portal so the hero's overflow-hidden can't crop it; positioned
  // against the input's viewport rect.
  const [mounted, setMounted] = useState(false);
  const [menuPos, setMenuPos] = useState<
    { top: number; left: number; width: number; maxHeight: number } | null
  >(null);

  useEffect(() => setMounted(true), []);

  const updateMenuPosition = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    // visualViewport accounts for the on-screen keyboard on mobile.
    const viewportH = window.visualViewport?.height ?? window.innerHeight;
    // Cap to the room below the input so the menu scrolls internally instead of overflowing the screen.
    const spaceBelow = viewportH - rect.bottom - 8;
    const maxHeight = Math.max(120, Math.min(288, spaceBelow));
    setMenuPos({ top: rect.bottom + 4, left: rect.left, width: rect.width, maxHeight });
  }, []);

  useEffect(() => {
    if (!showSuggestions) return;
    updateMenuPosition();
    // The menu scrolling within itself must not reposition the menu - on iOS
    // that repositioning fights the momentum scroll and the list feels stuck.
    const handleAncestorScroll = (event: Event) => {
      const target = event.target as Node | null;
      if (target && menuRef.current?.contains(target)) return;
      updateMenuPosition();
    };
    // capture:true catches scrolling of any ancestor, not just window.
    window.addEventListener('scroll', handleAncestorScroll, true);
    window.addEventListener('resize', updateMenuPosition);
    window.visualViewport?.addEventListener('resize', updateMenuPosition);
    window.visualViewport?.addEventListener('scroll', updateMenuPosition);
    return () => {
      window.removeEventListener('scroll', handleAncestorScroll, true);
      window.removeEventListener('resize', updateMenuPosition);
      window.visualViewport?.removeEventListener('resize', updateMenuPosition);
      window.visualViewport?.removeEventListener('scroll', updateMenuPosition);
    };
  }, [showSuggestions, updateMenuPosition]);

  const selectSuggestion = useCallback(
    (suggestion: string) => {
      clearBlurTimeout();
      setBreed(suggestion);
      setDisplayValue(suggestion);
      setSelectedValue(suggestion);
      setSearchTerm(suggestion);
      setShowSuggestions(false);
    },
    [setBreed]
  );

  const handleSearchChange = debounce((value: string) => {
    setSearchTerm(value);
  }, 100);

  const handleFocus = () => {
    clearBlurTimeout();
    setShowSuggestions(true);
  };

  const handleBlur = () => {
    clearBlurTimeout();
    blurTimeoutRef.current = setTimeout(() => {
      blurTimeoutRef.current = null;
      setShowSuggestions(false);
      if (selectedValue && displayValue.toLowerCase() === searchTerm.toLowerCase()) {
        setDisplayValue(selectedValue);
        setBreed(selectedValue);
      }
    }, 100);
  };

  const handleInputChange = (value: string) => {
    setDisplayValue(value);
    handleSearchChange(value);
    if (!value) {
      setBreed('');
    }
  };

  return (
    <div className="flex flex-col relative">
      <div className="relative w-full">
        <input
          type="text"
          disabled={!petType}
          placeholder="Search for breed"
          value={displayValue}
          ref={inputRef}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className="input input-bordered w-full border-primary pr-10 disabled:bg-base-content/10 z-10 relative"
        />
        <ChevronDown24Regular className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none" />
        {selectedValue && displayValue.toLowerCase() !== selectedValue.toLowerCase() && searchTerm.length > 0 && (
          <input
            type="text"
            value={selectedValue}
            className="absolute top-0 left-0 w-full h-full input input-bordered bg-transparent text-gray-400 pointer-events-none"
            readOnly
          />
        )}
      </div>
      {error && <div className="text-error">Error fetching breeds</div>}
      {mounted && showSuggestions && suggestions.length > 0 && menuPos &&
        createPortal(
          <ul
            ref={menuRef}
            style={{
              position: 'fixed',
              top: menuPos.top,
              left: menuPos.left,
              width: menuPos.width,
              maxHeight: menuPos.maxHeight,
              zIndex: 9999,
              // Tell the browser this list pans vertically, so it owns the
              // scroll gesture instead of leaving it to the tap handlers.
              touchAction: 'pan-y',
              WebkitOverflowScrolling: 'touch',
              overscrollBehavior: 'contain',
            }}
            className="bg-white border border-gray-300 rounded-md shadow-lg overflow-y-auto"
          >
            {suggestions.map((suggestion, index) => (
              <li
                key={index}
                className="p-2 cursor-pointer hover:bg-gray-200"
                // Selection commits on pointerup, and only when the finger
                // barely moved. Committing on pointerdown (as this used to)
                // meant the first touch of a scroll on iOS instantly picked
                // whichever breed happened to be under the finger.
                onPointerDown={(event) => {
                  // Keeps focus on the input so the blur handler cannot close
                  // the menu mid-gesture. This does not block scrolling -
                  // that is touch-action's job.
                  event.preventDefault();
                  pendingTapRef.current = {
                    pointerId: event.pointerId,
                    x: event.clientX,
                    y: event.clientY,
                    suggestion,
                  };
                }}
                onPointerMove={(event) => {
                  const pending = pendingTapRef.current;
                  if (!pending || pending.pointerId !== event.pointerId) return;
                  if (
                    Math.abs(event.clientX - pending.x) > TAP_SLOP_PX ||
                    Math.abs(event.clientY - pending.y) > TAP_SLOP_PX
                  ) {
                    pendingTapRef.current = null;
                  }
                }}
                onPointerUp={(event) => {
                  const pending = pendingTapRef.current;
                  pendingTapRef.current = null;
                  if (!pending || pending.pointerId !== event.pointerId) return;
                  selectSuggestion(pending.suggestion);
                }}
                // iOS fires pointercancel as soon as it claims the gesture for
                // scrolling, which is the clearest "this was not a tap" signal.
                onPointerCancel={() => {
                  pendingTapRef.current = null;
                }}
              >
                {suggestion}
              </li>
            ))}
          </ul>,
          document.body
        )}
    </div>
  );
};

export default SearchSelectBreed;